import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fmtDate = (d) => {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt.toLocaleDateString("no-NO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";
};

const TYPE_LABELS = {
  landevei: "Landevei",
  gravel: "Gravel",
  MTB: "MTB",
  FS: "FS",
  Hybrid: "Hybrid",
  barnesykkel: "Barnesykkel",
  "E-MTB": "E-MTB",
  "E-FS": "E-FS",
  "E-trekking": "E-trekking",
};

export default function AllBikesPage() {
  const navigate = useNavigate();
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    fetch(`${API_BASE_URL}/bikes/all`)
      .then((r) => {
        if (!r.ok) throw new Error("Kunne ikke hente sykkelkort");
        return r.json();
      })
      .then((data) => {
        setBikes(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  const filtered = useMemo(() => {
    let result = bikes;

    if (typeFilter) {
      result = result.filter((b) => b.type === typeFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((b) => {
        const kundeName = b.customer
          ? `${b.customer.firstName || ""} ${b.customer.lastName || ""}`.trim()
          : "";
        return (
          (b.merke || "").toLowerCase().includes(q) ||
          (b.modell || "").toLowerCase().includes(q) ||
          (b.rammenummer || "").toLowerCase().includes(q) ||
          (b.shortId || "").toLowerCase().includes(q) ||
          kundeName.toLowerCase().includes(q) ||
          (b.customer?.phoneNumber || b.phoneKey || "").includes(q)
        );
      });
    }

    return result;
  }, [bikes, search, typeFilter]);

  const typeOptions = useMemo(() => {
    const types = [...new Set(bikes.map((b) => b.type).filter(Boolean))];
    return types.sort();
  }, [bikes]);

  return (
    <div className="p-4 max-w-screen-xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Alle sykkelkort</h1>
        <span className="text-sm text-gray-500">{filtered.length} sykler</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          type="text"
          placeholder="Søk på merke, modell, rammenummer, kunde..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-56 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
        >
          <option value="">Alle typer</option>
          {typeOptions.map((t) => (
            <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="text-center py-12 text-gray-500">Laster sykkelkort...</div>
      )}

      {error && (
        <div className="text-center py-12 text-red-500">{error}</div>
      )}

      {!loading && !error && (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-100 text-sm text-gray-700">
              <tr>
                <th className="px-4 py-2 text-left hidden md:table-cell">ID</th>
                <th className="px-4 py-2 text-left">Merke</th>
                <th className="px-4 py-2 text-left">Modell</th>
                <th className="px-4 py-2 text-left hidden md:table-cell">Type</th>
                <th className="px-4 py-2 text-left hidden md:table-cell">Årsmodell</th>
                <th className="px-4 py-2 text-left hidden md:table-cell">Rammenummer</th>
                <th className="px-4 py-2 text-left">Kunde</th>
                <th className="px-4 py-2 text-left hidden md:table-cell">Telefon</th>
                <th className="px-4 py-2 text-right hidden md:table-cell">Opprettet</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-sm">
                    Ingen sykler funnet
                  </td>
                </tr>
              )}
              {filtered.map((b) => {
                const kundeName = b.customer
                  ? `${b.customer.firstName || ""} ${b.customer.lastName || ""}`.trim()
                  : "";
                const telefon = b.customer?.phoneNumber || b.phoneKey || "";

                return (
                  <tr
                    key={b._id}
                    className="border-t hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/sykkelkort/${b._id}`)}
                  >
                    <td className="px-4 py-2 font-mono text-xs hidden md:table-cell text-gray-500">
                      {b.shortId}
                    </td>
                    <td className="px-4 py-2 font-medium">{b.merke}</td>
                    <td className="px-4 py-2">{b.modell}</td>
                    <td className="px-4 py-2 hidden md:table-cell">
                      {TYPE_LABELS[b.type] || b.type || ""}
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell text-gray-600">
                      {b.årsmodell || ""}
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell font-mono text-xs text-gray-600">
                      {b.rammenummer || ""}
                    </td>
                    <td className="px-4 py-2">{kundeName}</td>
                    <td className="px-4 py-2 hidden md:table-cell text-sm text-gray-600">
                      {telefon}
                    </td>
                    <td className="px-4 py-2 text-right hidden md:table-cell text-sm text-gray-500">
                      {fmtDate(b.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
